import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { AnyAction } from "redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Table from "../../../core/common/dataTable/index";
import { fetchAllReimbursement } from "../../../core/data/redux/reimbursementSlice";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { all_routes } from "../../router/all_routes";

type AppDispatch = ThunkDispatch<any, any, AnyAction>;

const ReimbursementList = () => {
    const routes = all_routes;
    const dispatch = useDispatch<AppDispatch>();
    const reimbursementState = useSelector((state: any) => state.reimbursementState);

    const [dataList, setDataList] = useState<any[]>([]);
    const [query, setQuery] = useState<string>("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 10,
    });

    const fetchRecords = (pagination: any) => {
        dispatch(
            fetchAllReimbursement({
                page: pagination.current,
                limit: pagination.pageSize,
            })
        );
    };

    const handleRefresh = () => {
        fetchRecords(pagination);
    };

    const handlePagination = (newPagination: any, filters?: any, sorter?: any) => {
        setPagination((prev) => {
            const isPageSizeChanged = prev.pageSize !== newPagination.pageSize;
            const updatedPagination = {
                current: isPageSizeChanged ? 1 : newPagination.current || 1,
                pageSize: newPagination.pageSize,
                total: newPagination.total || prev.total,
            };
            fetchRecords(updatedPagination);
            return updatedPagination;
        });
    };

    const handleSearch = (searchQuery: string) => {
        setQuery(searchQuery);
        const resetPagination = {
            ...pagination,
            current: 1,
        };
        setPagination(resetPagination);
        fetchRecords(resetPagination);
    };

    useEffect(() => {
        fetchRecords(pagination);
    }, []);

    useEffect(() => {
        if (reimbursementState.loading === false && reimbursementState.requests) {
            const requests = reimbursementState.requests || [];
            setDataList(Array.isArray(requests) ? requests : []);
            setPagination({
                ...pagination,
                total: reimbursementState.pagination?.total || 0,
                current: reimbursementState.pagination?.page || 1,
                pageSize: reimbursementState.pagination?.limit || 10,
            });
        } else if (reimbursementState.error) {
            toast.error(reimbursementState.error || "Failed to fetch reimbursement requests");
        }
    }, [reimbursementState.loading, reimbursementState.requests, reimbursementState.pagination]);

    const columns = [
        {
            title: "Sr. No.",
            render: (_: any, __: any, index: number) =>
                index + 1 + (pagination.current - 1) * pagination.pageSize,
        },
        {
            title: "EmpID",
            dataIndex: "EmpID",
            render: (EmpID: string) => <span>{formatEmpIdForDisplay(EmpID || "")}</span>,
        },
        {
            title: "Reference Number",
            dataIndex: "ReferenceNumber",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Reimbursement Type",
            dataIndex: "ReimbursementType",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Request Type",
            dataIndex: "RequestType",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Request Status",
            dataIndex: "RequestStatus",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Phase Of Request",
            dataIndex: "PhaseOftheRequest",
            render: (text: string) => <span>{text || "-"}</span>,
        },
        {
            title: "Requested Amount",
            dataIndex: "RequestedAmount",
            render: (text: string) => <span>{text ? `₹${parseFloat(text).toLocaleString('en-IN')}` : "-"}</span>,
        },
        {
            title: "Approved Amount",
            dataIndex: "ApprovedAmount",
            render: (text: string) => <span>{text ? `₹${parseFloat(text).toLocaleString('en-IN')}` : "-"}</span>,
        },
        {
            title: "Approver User ID",
            dataIndex: "ApproverUserID",
            render: (text: string) => <span>{formatEmpIdForDisplay(text || "")}</span>,
        },
        {
            title: "Request Creation Date",
            dataIndex: "RequestCreationDate",
            render: (text: string) =>
                text ? new Date(text).toLocaleDateString() : "N/A",
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            render: (text: string) =>
                text ? new Date(text).toLocaleString() : "N/A",
        },
    ];

    return (
        <>
            <style>
                {`
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
            </style>
            <div className="page-wrapper">
                <div className="content">
                    <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
                        <div className="my-auto mb-2">
                            <h3 className="page-title mb-1">Reimbursement</h3>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={routes.adminDashboard}>Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to="#">Services</Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Reimbursement
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
                            <div className="mb-2 me-2">
                                <Link
                                    to={routes.reimbursementSync}
                                    className="btn btn-outline-primary d-flex align-items-center"
                                    title="Reimbursement Sync"
                                >
                                    <i className="ti ti-refresh me-2" />
                                    Sync Data
                                </Link>
                            </div>
                            <div className="mb-2">
                                <button
                                    className="btn btn-primary d-flex align-items-center"
                                    onClick={handleRefresh}
                                    disabled={reimbursementState.loading === true}
                                    title="Refresh"
                                >
                                    <i
                                        className={`ti ti-refresh me-2 ${reimbursementState.loading === true ? "spin" : ""
                                            }`}
                                    />
                                    {reimbursementState.loading === true ? "Refreshing..." : "Refresh"}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                            <h4 className="mb-4 mt-3">Reimbursement</h4>
                        </div>
                        <div className="card-body p-0 py-3">
                            <Table
                                columns={columns}
                                dataSource={dataList}
                                Selection={false}
                                paginationConfig={pagination}
                                handlePagination={handlePagination}
                                loading={reimbursementState.loading === true || reimbursementState.loading === "pending"}
                                handleSearch={handleSearch}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ReimbursementList;

